import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API_URL } from "../../config.js";
import Notification from "../../components/Notification.js";
import { allowedToView } from "../../utils/PermissionUtils.js";
import { formatToDayMonthYear, formatTimestamp } from "../../utils/TextUtils.js";

function Debts() {
    const [debts, setDebts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(null);
    const [notification, setNotification] = useState({ type: "", message: "" });
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [filterDate, setFilterDate] = useState(""); // Filter by specific date
    const [filterMonth, setFilterMonth] = useState(""); // Filter by month and year
    const perPage = 10; // Items per page
    const token = localStorage.getItem("access_token");
    const userRole = localStorage.getItem("userRole");
    const canEdit = (userRole) => {
        return (allowedToView(userRole, ['admin', 'superadmin']));
    };

    const canAdd = (userRole) => {
        return (allowedToView(userRole, ['admin', 'superadmin', 'user']));
    };


    const navigate = useNavigate();

    // Memoize fetchDebts to prevent unnecessary re-renders
    const fetchDebts = useCallback(async () => {
        try {
            setLoading(true);
            let url = `${API_URL}/debts?page=${currentPage}&limit=${perPage}`;
            if (searchTerm) {
                url += `&search=${searchTerm}`;
            }
            if (filterDate) {
                url += `&date=${filterDate}`;
            }
            if (filterMonth) {
                url += `&month=${filterMonth}`;
            }

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setDebts(data.data.data || []);
                setTotalPages(data.data.last_page || 1);
            } else if (response.status === 401) {
                navigate("/logout");
            } else {
                throw new Error("Failed to fetch debts");
            }
        } catch (error) {
            setError("An error occurred while fetching debts.");
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, searchTerm, filterDate, filterMonth, navigate, token]);

    // Fetch debts when dependencies change
    useEffect(() => {
        fetchDebts();
    }, [fetchDebts]);

    const toggleDropdown = (index) => {
        if (isDropdownOpen === index) {
            setIsDropdownOpen(null); // Close dropdown if the same one is clicked
        } else {
            setIsDropdownOpen(index); // Open the clicked dropdown
        }
    };

    
    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1); // Reset to the first page when searching
    };

    const handleFilterDateChange = (event) => {
        setFilterDate(event.target.value);
        setCurrentPage(1); // Reset to the first page when applying a new filter
    };

    const handleFilterMonthChange = (event) => {
        setFilterMonth(event.target.value);
        setCurrentPage(1); // Reset to the first page when applying a new filter
    };

    const getStatusTag = (status) => {
        switch (status) {
            case 'UnPaid':
                return { className: 'is-danger', text: 'UnPaid' };
            case 'Partly Paid':
                return { className: 'is-warning', text: 'Partly Paid' };
            case 'Cleared':
                return { className: 'is-success', text: 'Cleared' };
            default:
                return { className: '', text: status };
        }
    };

    // Handle click outside dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isDropdownOpen !== null) {
                const dropdown = document.querySelector(`.dropdown.is-active`);
                if (dropdown && !dropdown.contains(event.target)) {
                    setIsDropdownOpen(null);
                }
            }
        };

        document.addEventListener("click", handleClickOutside);

        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, [isDropdownOpen]);

    return (
        <div>
            <main>
                <nav className="level">
                    <div className="level-left">
                        <div className="level-item">
                            <p className="title">Debts</p>
                        </div>
                    </div>
                    <div className="level-right">
                        {canAdd(userRole) && (
                            <p className="level-item">
                                <Link to="add" className="button primary-bg">
                                    <i className="fa fa-plus" aria-hidden="true"></i> &nbsp; New
                                </Link>
                            </p>
                        )}
                    </div>
                </nav>

                <h1>Debt List</h1>

                {notification.message && (
                    <Notification
                        type={notification.type}
                        message={notification.message}
                        onClose={closeNotification}
                    />
                )}

                {/* Filters */}
                <div className="field is-horizontal">
                    <div className="field-body">
                        <div className="field">
                            <label className="label">Search by Farmer Name or Framer Number</label>
                            <div className="control">
                                <input
                                    className="input"
                                    type="text"
                                    placeholder="Search by farmer name or farmer number"
                                    value={searchTerm}
                                    onChange={handleSearch}
                                />
                            </div>
                        </div>
                        <div className="field">
                            <label className="label">Filter by Date</label>
                            <div className="control">
                                <input
                                    type="date"
                                    className="input"
                                    value={filterDate}
                                    onChange={handleFilterDateChange}
                                />
                            </div>
                        </div>
                        <div className="field">
                            <label className="label">Filter by Month</label>
                            <div className="control">
                                <input
                                    type="month"
                                    className="input"
                                    value={filterMonth}
                                    onChange={handleFilterMonthChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <p>Loading debts...</p>
                ) : error ? (
                    <p className="has-text-danger">{error}</p>
                ) : (
                    <>
                        <table className="table is-striped is-fullwidth">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Farmer Name</th>
                                    <th>Product</th>
                                    <th>Amount</th>
                                    <th>Repaid Amount</th>
                                    <th>Balance Due</th>
                                    <th>Date Taken</th>
                                    <th>Status</th>
                                    <th>Posted At</th>
                                    <th>Options</th>
                                </tr>
                            </thead>
                            <tbody>
                                {debts.length > 0 ? (
                                    debts.map((debt, index) => (
                                        <tr key={debt.id}>
                                            <td>{(currentPage - 1) * perPage + index + 1}</td>
                                            <td>
                                                <Link to={`/dashboard/farmers/${debt?.farmer?.id}/details`} className="primary-text-color"> {debt?.farmer?.first_name} {debt?.farmer?.middle_name} {debt?.farmer?.last_name} ( {debt?.farmer?.farmer_number} ) </Link>
                                            <br/> <small className="has-text-grey">SR # {debt.serial_number}</small>
                                            </td>
                                            <td>{debt.product?.name || "N/A"}</td>
                                            <td>{debt.amount}</td>
                                            <td>{debt.deducted_amount}</td>
                                            <td>{debt.balance_due}</td>
                                            <td>{formatToDayMonthYear(debt.date_taken)}</td>
                                            <td>
                                                <span className={`tag ${getStatusTag(debt.status).className}`}>
                                                    {getStatusTag(debt.status).text}
                                                </span>
                                            </td>
                                            <td>{formatTimestamp(debt.created_at) || "N/A"}</td>
                                            <td>
                                                <div className={`dropdown is-right ${isDropdownOpen === index ? 'is-active' : ''}`}>
                                                    <div className="dropdown-trigger">
                                                        <button
                                                            className="button is-white"
                                                            aria-haspopup="true"
                                                            aria-controls="dropdown-menu"
                                                            onClick={() => toggleDropdown(index)}
                                                        >
                                                            <span className="icon">
                                                                <i className="fas fa-ellipsis-v"></i>
                                                            </span>
                                                        </button>
                                                    </div>
                                                    <div className="dropdown-menu" id="dropdown-menu" role="menu">
                                                        <div className="dropdown-content">
                                                            <Link to={`${debt.id}/details`} className="dropdown-item">
                                                                <span className="icon">
                                                                    <i className="fas fa-eye"></i>
                                                                </span>
                                                                <span>View</span>
                                                            </Link>
                                                            {canEdit(userRole) && (
                                                                <>
                                                                    <Link to={`${debt.id}/update`} className="dropdown-item">
                                                                        <span className="icon">
                                                                            <i className="fas fa-edit"></i>
                                                                        </span>
                                                                        <span>Edit</span>
                                                                    </Link>
                                                                   
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                           
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="10" className="has-text-centered">No debts found</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        {/* Pagination Controls */}
                        <nav className="pagination is-centered">
                            <button
                                className="pagination-previous"
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                            >
                                Previous
                            </button>
                            <span className="pagination-page">Page {currentPage} of {totalPages}</span>
                            <button
                                className="pagination-next"
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                            >
                                Next
                            </button>
                        </nav>
                    </>
                )}
            </main>
        </div>
    );
}

export default Debts;